<script setup>
import { ref, computed } from 'vue'
import { useGlobalStore } from '@stores/global'

const props = defineProps({
	item: {
		type: Object,
		required: true,
	},
	depth: {
		type: Number,
		default: 0,
	},
})

const global = useGlobalStore()

const open = ref(false)
const hasChildren = computed(() => props.item.children?.length > 0)
const isTop = computed(() => props.depth === 0)

const closeMobile = () => global.toggleMobile(false)

const rowClass = computed(() => {
	if (isTop.value) {
		return ['flex px-2 py-1 transition-colors duration-200 hover:text-snk-blue-500']
	}

	return ['flex py-2.5 pr-4 text-sm transition-colors duration-200 hover:bg-snk-light-900/40 hover:text-snk-blue-500']
})

const rowStyle = computed(() => (isTop.value ? null : { paddingLeft: `${props.depth}rem` }))
</script>

<template>
	<li :class="isTop ? 'font-poppins text-base font-semibold uppercase text-snk-dark-300' : ''">
		<!-- Accordion -->
		<template v-if="hasChildren">
			<button type="button" class="w-full items-center justify-between uppercase" :class="[...rowClass, 'flex']" :style="rowStyle" @click="open = !open">
				<span class="flex items-center gap-1.5">
					{{ item.label }}
					<i v-if="item.showIcon" class="fas fa-arrow-up-right-from-square -mt-1 text-xs" />
				</span>
				<i class="fas fa-chevron-down text-xs transition-transform duration-200" :class="{ 'rotate-180': open }" />
			</button>

			<ul v-show="open" class="mt-1 flex flex-col overflow-hidden rounded-md border border-snk-light-900">
				<MobileMenuItem v-for="(child, i) in item.children" :key="child.label + i" :item="child" :depth="depth + 1" />
			</ul>
		</template>

		<!-- Leaf item -->
		<template v-else>
			<router-link v-if="item.type === 'router'" :class="rowClass" :style="rowStyle" :to="item.to" exact-active-class="text-snk-blue-500" @click="closeMobile">
				{{ item.label }}
			</router-link>
			<a v-else-if="item.type === 'external'" class="items-center gap-1.5" :class="rowClass" :style="rowStyle" :href="item.href" :target="item.target" @click="closeMobile">
				{{ item.label }}
				<i v-if="item.showIcon" class="fas fa-arrow-up-right-from-square -mt-1 text-xs" />
			</a>
			<span v-else :class="rowClass" :style="rowStyle">{{ item.label }}</span>
		</template>
	</li>
</template>

<style scoped>
@reference '../../../css/theme.css';
</style>
